import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import type { PluginListenerHandle } from '@capacitor/core';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

/**
 * Push Notifications Hook
 *
 * Handles the full push notification lifecycle:
 * 1. Request permission after user authenticates
 * 2. Register device token with Supabase push_tokens table
 * 3. Handle token refresh
 * 4. Handle notification tap (navigate via deep link)
 * 5. Handle foreground notifications (show toast, don't duplicate system notification)
 * 6. Clean up token on sign out
 *
 * The `pushNotificationActionPerformed` listener is registered immediately on
 * mount (independent of auth) to capture cold-start notification taps that
 * fire before authentication resolves.
 *
 * Only runs on native platforms (iOS/Android), no-op on web.
 */
export function usePushNotifications() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const registeredRef = useRef(false);
  const currentTokenRef = useRef<string | null>(null);
  const userRef = useRef(user);
  const pendingLinkRef = useRef<{ link: string; timestamp: number } | null>(null);
  const actionListenerRegisteredRef = useRef(false);
  const navigateRef = useRef(navigate);

  // Keep refs in sync with latest values
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  // Register pushNotificationActionPerformed listener ONCE on mount,
  // independent of auth state, to catch cold-start taps.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    if (actionListenerRegisteredRef.current) return;
    actionListenerRegisteredRef.current = true;

    PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      console.log('[PushNotifications] Action performed (cold-start safe):', action);
      const data = action.notification.data;
      if (data?.link) {
        if (userRef.current) {
          // Defer navigation to ensure router is settled after cold start
          setTimeout(() => navigateRef.current(data.link), 100);
        } else {
          console.log('[PushNotifications] User not yet authenticated, storing pending link:', data.link);
          pendingLinkRef.current = { link: data.link, timestamp: Date.now() };
        }
      }
    });

    // This listener persists for the app lifetime - no cleanup needed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When user becomes available, process any pending deep link
  useEffect(() => {
    if (user && pendingLinkRef.current) {
      const { link, timestamp } = pendingLinkRef.current;
      pendingLinkRef.current = null;
      // Only process if the pending link is less than 30 seconds old
      const MAX_PENDING_AGE_MS = 30_000;
      if (Date.now() - timestamp < MAX_PENDING_AGE_MS) {
        console.log('[PushNotifications] Processing pending deep link:', link);
        // Defer to ensure router/auth are fully settled
        setTimeout(() => navigateRef.current(link), 100);
      } else {
        console.log('[PushNotifications] Discarding stale pending deep link:', link);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    if (!user) {
      // User signed out - deactivate token
      if (currentTokenRef.current) {
        deactivateToken(currentTokenRef.current);
        currentTokenRef.current = null;
        registeredRef.current = false;
      }
      return;
    }

    // Don't auto-request permission - the PostSignupFlow handles that.
    // Instead, just set up listeners if permission was already granted.
    if (registeredRef.current) return;

    // Track auth-dependent listener handles for targeted cleanup
    const authListenerHandles: PluginListenerHandle[] = [];

    setupListenersAndRegisterIfGranted(authListenerHandles);

    return () => {
      // Only remove auth-dependent listeners; the action listener persists.
      authListenerHandles.forEach((handle) => handle.remove());
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  /**
   * Sets up auth-dependent listeners and registers ONLY if permission is already granted.
   * Does NOT prompt the user - the onboarding flow handles that separately.
   * Note: pushNotificationActionPerformed is handled separately above.
   */
  async function setupListenersAndRegisterIfGranted(handles: PluginListenerHandle[]) {
    try {
      const permStatus = await PushNotifications.checkPermissions();

      if (permStatus.receive !== 'granted') {
        console.log('[PushNotifications] Permission not yet granted, skipping auto-register');
        return;
      }

      registeredRef.current = true;

      // IMPORTANT: Attach listeners BEFORE register() to avoid race condition
      const regHandle = await PushNotifications.addListener('registration', async (token) => {
        console.log('[PushNotifications] Registration successful, token:', token.value.substring(0, 20) + '...');
        currentTokenRef.current = token.value;
        await saveToken(token.value);
      });
      handles.push(regHandle);

      const errHandle = await PushNotifications.addListener('registrationError', (error) => {
        console.error('[PushNotifications] Registration error:', error);
      });
      handles.push(errHandle);

      const fgHandle = await PushNotifications.addListener('pushNotificationReceived', (notification) => {
        console.log('[PushNotifications] Received in foreground:', notification);
        toast(notification.title || 'New notification', {
          description: notification.body || undefined,
        });
      });
      handles.push(fgHandle);

      await PushNotifications.register();
    } catch (error) {
      console.error('[PushNotifications] Setup error:', error);
    }
  }

  async function saveToken(token: string) {
    if (!user) return;

    try {
      const platform = Capacitor.getPlatform(); // 'ios' or 'android'

      // Validate token format before saving
      if (platform === 'ios' && !/^[a-f0-9]{64}$/i.test(token)) {
        console.error('[PushNotifications] Invalid APNs token format:', token.substring(0, 20) + '...');
        return;
      }
      if (platform === 'android' && token.length < 100) {
        console.error('[PushNotifications] Invalid FCM token format: too short');
        return;
      }

      // First, remove this token from ALL other users (device changed hands)
      await supabase
        .from('push_tokens')
        .delete()
        .eq('token', token)
        .neq('user_id', user.id);

      // Clean up any OTHER tokens for the same user+platform (stale tokens from reinstalls)
      await supabase
        .from('push_tokens')
        .delete()
        .eq('user_id', user.id)
        .eq('platform', platform)
        .neq('token', token);

      // Then upsert for current user
      const { error } = await supabase
        .from('push_tokens')
        .upsert(
          {
            user_id: user.id,
            token,
            platform,
          },
          {
            onConflict: 'user_id,token',
          }
        );

      if (error) {
        console.error('[PushNotifications] Error saving token:', error);
      } else {
        console.log('[PushNotifications] Token saved successfully');
      }
    } catch (error) {
      console.error('[PushNotifications] Error saving token:', error);
    }
  }

  async function deactivateToken(token: string) {
    try {
      const { error } = await supabase
        .from('push_tokens')
        .delete()
        .eq('token', token);

      if (error) {
        console.error('[PushNotifications] Error deactivating token:', error);
      } else {
        console.log('[PushNotifications] Token deactivated');
      }
    } catch (error) {
      console.error('[PushNotifications] Error deactivating token:', error);
    }
  }
}
